import React from "react";
import PropTypes from "prop-types";
import QueueAnim from "rc-queue-anim";
import { push } from "connected-react-router";
import RaisedButton from "@mui/material/Button";
import { withStyles } from "@mui/styles";
import CircularProgress from "components/EnhancedCircularProgress";
import EnhancedSnackBar from "components/EnhancedSnackBar";
import { authLogout } from "stores/actions/auth";
import { logout } from "services/authService";
import { isEmpty } from "utils/helpers";
import {
  resolveCashboxApprovalLink,
  fetchCashboxApproval
} from "services/cashboxApprovalService";
import {
  CASHBOX_APPROVAL_TOKEN_KEY,
  CASHBOX_APPROVAL_EMAILS_KEY
} from "constants/Paths";
import { attemptAppHandoff } from "./appHandoff";
import ConfirmResponse from "./ConfirmResponse";
import WrongAccount from "./WrongAccount";
import LinkExpired from "./LinkExpired";
import {
  LINK_STATE_READY,
  LINK_STATE_LOGIN_REQUIRED,
  APPROVAL_STATUS_APPROVED,
  APPROVAL_STATUS_REJECTED
} from "./constants";
import { styles } from "./styles";

const renderHeader = () => (
  <div className="row">
    <div className="col-12">
      <h1 style={{ textAlign: "right", margin: "0 0 5px 0" }}>
        <a href="https://wakilni.com/">
          <img
            src="/assets/images/wakilni-logo-v2.png"
            height="25"
            alt={"Wakilni"}
          />
        </a>
      </h1>
    </div>
  </div>
);

/**
 * The page the WhatsApp approval link opens. Part of the portal, but outside
 * /app and in no menu: it is reached only through that link.
 *
 * A dead link — answered, expired, a failed delivery, or a token that never
 * existed — renders one Link Expired card, the same card for every case. A
 * missing session lands on the ordinary login page, unadorned; a session
 * belonging to somebody else is named as such.
 */
class CashboxApproval extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      isLoading: true,
      is_dead: false,
      wrong_account: false,
      shop: null,
      order_id: null,
      approval: null,
      answered: false,
      // null while no modal is open, otherwise true for approve, false for reject.
      is_approve: null
    };
    this._isMounted = false;
    this._signingOut = false;
    this._loginEmails = [];
  }

  componentDidMount() {
    this._isMounted = true;
    sessionStorage.removeItem(CASHBOX_APPROVAL_TOKEN_KEY);
    sessionStorage.removeItem(CASHBOX_APPROVAL_EMAILS_KEY);
    // The app is an optimisation, never a requirement: the card below is a
    // complete fallback, so the page carries on loading either way.
    attemptAppHandoff(this.props.match.params.token);
    this.resolveLink();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  resolveLink = () => {
    const token = this.props.match.params.token;

    this.props
      .dispatch(resolveCashboxApprovalLink(token))
      .then(data => {
        if (!this._isMounted) {
          return;
        }

        if (data.state === LINK_STATE_READY) {
          this.loadApproval(data.order_id);
          return;
        }

        if (data.state === LINK_STATE_LOGIN_REQUIRED) {
          this._loginEmails = Array.isArray(data.emails) ? data.emails : [];

          if (data.wrong_account) {
            this.setState({
              isLoading: false,
              wrong_account: true,
              shop: data.shop || null
            });
            return;
          }

          this.signOutToLogin();
          return;
        }

        this.showDead();
      })
      .catch(() => this.showDead());
  };

  loadApproval = order_id => {
    this.props
      .dispatch(fetchCashboxApproval(order_id))
      .then(data => {
        if (!this._isMounted) {
          return;
        }

        if (!data.show_approval_card) {
          this.showDead();
          return;
        }

        this.setState({
          isLoading: false,
          order_id,
          approval: data.cashbox_approval
        });
      })
      .catch(() => this.showDead());
  };

  signOutToLogin = () => {
    const { user } = this.props;

    if (this._signingOut) {
      return;
    }
    this._signingOut = true;

    sessionStorage.setItem(
      CASHBOX_APPROVAL_TOKEN_KEY,
      this.props.match.params.token
    );
    sessionStorage.setItem(
      CASHBOX_APPROVAL_EMAILS_KEY,
      JSON.stringify(this._loginEmails)
    );

    const toLogin = () => this.props.dispatch(push("/login"));

    if (!user || isEmpty(user)) {
      this.props.dispatch(authLogout());
      toLogin();
      return;
    }

    this.props
      .dispatch(logout())
      .then(toLogin)
      .catch(() => {
        this.props.dispatch(authLogout());
        toLogin();
      });
  };

  showDead = () => {
    sessionStorage.removeItem(CASHBOX_APPROVAL_TOKEN_KEY);
    sessionStorage.removeItem(CASHBOX_APPROVAL_EMAILS_KEY);
    this.setState({
      isLoading: false,
      is_dead: true,
      wrong_account: false,
      approval: null
    });
  };

  handleOpen = is_approve => () => {
    this.setState({ is_approve });
  };

  handleClose = () => {
    this.setState({ is_approve: null });
  };

  handleAnswered = approval => {
    this.setState({ approval, answered: true, is_approve: null });
  };

  /**
   * A refused answer usually means the request moved while the page was open —
   * answered elsewhere, or the driver failed the delivery. Reload the card and
   * show whatever it now says rather than leaving a dead button on screen.
   */
  handleFailed = () => {
    this.setState({ is_approve: null });

    this.props
      .dispatch(fetchCashboxApproval(this.state.order_id))
      .then(data => {
        if (!this._isMounted) {
          return;
        }

        if (!data.cashbox_approval) {
          this.showDead();
          return;
        }

        this.setState({
          approval: data.cashbox_approval,
          answered: !data.show_approval_card
        });
      })
      .catch(() => {});
  };

  renderAmounts = () => {
    const { classes } = this.props;

    return (
      <div className={classes.amount_row}>
        {this.state.approval.amounts.map(amount => (
          <span key={amount.currency_id} className={classes.amount_item}>
            <span className={classes.amount_value}>
              {Number(amount.amount).toLocaleString()}
            </span>
            <span className={classes.amount_currency}>{amount.currency}</span>
          </span>
        ))}
      </div>
    );
  };

  renderAnswer = () => {
    const { classes } = this.props;
    const { approval } = this.state;
    const is_approved = approval.status === APPROVAL_STATUS_APPROVED;
    const is_rejected = approval.status === APPROVAL_STATUS_REJECTED;

    return (
      <div className="text-center">
        <span
          className={"material-icons " + classes.result_icon}
          style={{
            color: is_rejected ? "#f44336" : is_approved ? "#4caf50" : "#9e9e9e"
          }}
        >
          {is_rejected ? "cancel" : is_approved ? "check_circle" : "info"}
        </span>
        <h5 className="mt-0">{approval.message}</h5>
      </div>
    );
  };

  renderRequest = () => {
    const { classes } = this.props;
    const { approval } = this.state;

    return (
      <div>
        <div style={{ margin: "20px 0" }}>{this.renderAmounts()}</div>
        <div className="row">
          <div className="col-6">
            <RaisedButton
              variant="contained"
              fullWidth
              size="large"
              className={classes.reject_button}
              onClick={this.handleOpen(false)}
              disabled={!approval.can_respond}
            >
              Reject
            </RaisedButton>
          </div>
          <div className="col-6">
            <RaisedButton
              variant="contained"
              fullWidth
              size="large"
              className={classes.approve_button}
              onClick={this.handleOpen(true)}
              disabled={!approval.can_respond}
            >
              Approve
            </RaisedButton>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { classes, user } = this.props;
    const {
      isLoading,
      is_dead,
      wrong_account,
      shop,
      approval,
      answered,
      is_approve,
      order_id
    } = this.state;

    if (is_dead) {
      return (
        <div
          className="container-fluid page-dashboard"
          style={{ height: "auto" }}
        >
          <QueueAnim type="bottom" className="ui-animate">
            <div key="1">
              <article className="article">
                {renderHeader()}
                <LinkExpired />
              </article>
            </div>
          </QueueAnim>
          <EnhancedSnackBar />
        </div>
      );
    }

    if (wrong_account) {
      return (
        <div
          className="container-fluid page-dashboard"
          style={{ height: "auto" }}
        >
          <QueueAnim type="bottom" className="ui-animate">
            <div key="1">
              <article className="article">
                {renderHeader()}
                <WrongAccount
                  shop={shop}
                  current_email={user && user.email ? user.email : null}
                  handleSignInWithAnotherAccount={this.signOutToLogin}
                />
              </article>
            </div>
          </QueueAnim>
          <EnhancedSnackBar />
        </div>
      );
    }

    return (
      <div className="container-fluid page-dashboard" style={{ height: "auto" }}>
        <QueueAnim type="bottom" className="ui-animate">
          {isLoading ? (
            <div key="1" style={{ height: "100%" }}>
              <CircularProgress />
            </div>
          ) : (
            <div key="1">
              <article className="article">
                {renderHeader()}
                <div className={"box box-default " + classes.card}>
                  <div className="box-body">
                    <h2 className="article-title" style={{ margin: "0 0 5px 0" }}>
                      Please approve you received the Cashbox #{approval.cashbox_id} amount for Order #{approval.order_id}:
                    </h2>
                    <div
                      className="box-divider"
                      style={{ margin: "15px 0" }}
                    />
                    {answered ? this.renderAnswer() : this.renderRequest()}
                  </div>
                </div>
              </article>
            </div>
          )}
        </QueueAnim>
        {is_approve !== null && (
          <ConfirmResponse
            order_id={order_id}
            is_approve={is_approve}
            amounts={approval.amounts}
            handleAnswered={this.handleAnswered}
            handleFailed={this.handleFailed}
            handleClose={this.handleClose}
          />
        )}
        <EnhancedSnackBar />
      </div>
    );
  }
}

CashboxApproval.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  user: PropTypes.object,
  dispatch: PropTypes.func.isRequired
};

export default withStyles(styles)(CashboxApproval);
